import { notification, App } from 'antd';  
import { createContext, useState, useContext, useEffect, useRef, useCallback } from 'react';
import HtmlContent from '../components/HtmlContent';
import apiService, { setNotificationApi } from '../services/apiService';
import websocketService from '../services/websocketService';
import { useSelector } from 'react-redux'; // Import useSelector to get current user
import moment from 'moment';
export const NotificationContext = createContext();

const DEFAULT_NOTIFICATION_PREFS = { mutedTypes: [], desktopEnabled: true, soundEnabled: false };

const normalizeNotificationPreferences = (response) => {
  const prefs = response?.data?.mutedTypes || response?.data?.desktopEnabled !== undefined
    ? response.data
    : response?.data?.data || response;

  return {
    mutedTypes: Array.isArray(prefs?.mutedTypes) ? prefs.mutedTypes.map(String) : [],
    desktopEnabled: prefs?.desktopEnabled !== false,
    soundEnabled: prefs?.soundEnabled === true
  };
};

export const NotificationProvider = ({ children }) => {
  // Get current user from Redux store
  const currentUser = useSelector((state) => state.auth.user);
  const userId = currentUser?.id || currentUser?._id;

  // Create user-specific localStorage keys
  const getUserStorageKey = (key) => {
    return userId ? `${key}_${userId}` : key;
  };

  const [notifications, setNotifications] = useState([]);
  const [notifPrefs, setNotifPrefs] = useState(DEFAULT_NOTIFICATION_PREFS);
  const notifPrefsRef = useRef(DEFAULT_NOTIFICATION_PREFS);

  useEffect(() => {
    notifPrefsRef.current = notifPrefs;
  }, [notifPrefs]);

  const loadNotificationPreferences = useCallback(async () => {
    if (!userId) return DEFAULT_NOTIFICATION_PREFS;
    const response = await apiService.user.getNotificationPreferences();
    const prefs = normalizeNotificationPreferences(response);
    setNotifPrefs(prefs);
    return prefs;
  }, [userId]);

  const saveNotificationPreferences = useCallback(async (preferences) => {
    const prefs = normalizeNotificationPreferences(preferences);
    await apiService.user.updateNotificationPreferences(prefs);
    setNotifPrefs(prefs);
    return prefs;
  }, []);

  // Fetch notification preferences on login
  useEffect(() => {
    if (!userId) return;
    loadNotificationPreferences()
      .catch((e) => console.error('Notification preferences load failed:', e));
  }, [userId, loadNotificationPreferences]);
  const [unreadCount, setUnreadCount] = useState(() => {
    const storedCount = localStorage.getItem(getUserStorageKey('unreadCount'));
    return storedCount ? parseInt(storedCount, 10) : 0;
  });
  const [credentialsConfigured, setCredentialsConfigured] = useState(null); // null = unknown, true/false = known
  const credentialsConfiguredRef = useRef(credentialsConfigured);
  const [localNotificationApi, setLocalNotificationApi] = useState(null); // Renamed local state
  const [notificationMetadata, setNotificationMetadata] = useState(null);
  const [notificationFilters, setNotificationFilters] = useState({
    type: null,
    language: null,
    status: null,
    dateFrom: null,
    dateTo: null,
    pageNo: 1,
    pageSize: 20
  });
  const [isLoadingNotifications, setIsLoadingNotifications] = useState(false);

  useEffect(() => {
    credentialsConfiguredRef.current = credentialsConfigured;
  }, [credentialsConfigured]);

  // Use the hook to get the context-aware API and contextHolder
  const [api, contextHolder] = notification.useNotification();

  // Ref to store the last shown error notification details
  const lastErrorNotificationRef = useRef({ message: null, description: null });

  // Track known notification IDs to detect new arrivals for desktop push
  const knownNotificationIdsRef = useRef(new Set());

  // Critical LHDN type IDs that trigger desktop push notifications
  const DESKTOP_PUSH_TYPE_IDS = ['8', '15', '3'];
  const DOCUMENT_NOTIFICATION_TYPE_IDS = new Set(['6', '7', '8', '15']);

  const stripHtml = (value = '') => String(value).replace(/<[^>]*>/g, ' ');

  const extractInvoiceReference = (notificationData = {}) => {
    const directReference = notificationData.invoiceCodeNumber ||
      notificationData.invoiceNumber ||
      notificationData.internalId ||
      notificationData.documentNumber ||
      notificationData.documentId ||
      notificationData.uuid;
    if (directReference) return String(directReference).trim();

    const text = [
      notificationData.notificationSubject,
      notificationData.finalMessage,
      notificationData.message,
      notificationData.title
    ].map(stripHtml).join(' ');

    const labelledMatch = text.match(/(?:invoice|document)\s*(?:no\.?|number|id|reference)?\s*[:#-]?\s*([A-Z0-9][A-Z0-9/_-]{3,})/i);
    if (labelledMatch) return labelledMatch[1].trim();

    const fallbackMatch = text.match(/\b[A-Z]{2,}[A-Z0-9]*[-/]?\d{4,}[A-Z0-9/_-]*\b/i);
    return fallbackMatch ? fallbackMatch[0].trim() : null;
  };

  const getNotificationTimestamp = (notification) => {
    return notification.timestamp ||
      notification.originalData?.creationDateTime ||
      notification.originalData?.receivedDateTime ||
      notification.originalData?.deliveredDateTime ||
      new Date().toISOString();
  };

  const getNotificationPriority = (notification) => {
    const typeId = String(notification.originalData?.typeId || notification.originalData?.notificationTypeId || '');
    if (typeId === '8' || typeId === '15') return 4;
    if (typeId === '7') return 3;
    if (typeId === '6') return 2;
    return 1;
  };

  const groupDocumentNotifications = (notificationList) => {
    const groups = new Map();
    const passthrough = [];

    notificationList.forEach(notificationItem => {
      const typeId = String(notificationItem.originalData?.typeId || notificationItem.originalData?.notificationTypeId || '');
      const invoiceReference = extractInvoiceReference(notificationItem.originalData);
      if (!DOCUMENT_NOTIFICATION_TYPE_IDS.has(typeId) || !invoiceReference) {
        passthrough.push(notificationItem);
        return;
      }

      const groupKey = `${typeId === '8' || typeId === '15' ? typeId : 'submitted'}:${invoiceReference.toUpperCase()}`;
      const existing = groups.get(groupKey);
      const currentRank = getNotificationPriority(notificationItem);
      const existingRank = existing ? getNotificationPriority(existing) : 0;
      const currentTime = moment(getNotificationTimestamp(notificationItem)).valueOf();
      const existingTime = existing ? moment(getNotificationTimestamp(existing)).valueOf() : 0;

      if (!existing || currentRank > existingRank || (currentRank === existingRank && currentTime > existingTime)) {
        groups.set(groupKey, {
          ...notificationItem,
          id: existing?.id || notificationItem.id,
          groupedIds: [
            ...(existing?.groupedIds || [existing?.id].filter(Boolean)),
            notificationItem.id
          ].filter(Boolean),
          groupedCount: existing ? (existing.groupedCount || 1) + 1 : 1,
          read: existing ? existing.read && notificationItem.read : notificationItem.read
        });
      } else {
        groups.set(groupKey, {
          ...existing,
          groupedIds: [
            ...(existing.groupedIds || [existing.id].filter(Boolean)),
            notificationItem.id
          ].filter(Boolean),
          groupedCount: (existing.groupedCount || 1) + 1,
          read: existing.read && notificationItem.read
        });
      }
    });

    return [...passthrough, ...groups.values()]
      .sort((a, b) => moment(getNotificationTimestamp(b)).valueOf() - moment(getNotificationTimestamp(a)).valueOf());
  };

  // Set the notification API instance after the component mounts
  useEffect(() => {
    if (api) {
      setLocalNotificationApi(api); // Set local state
      setNotificationApi(api); // Call the imported function to set apiService's notificationApi
    }
  }, [api]);

  const isTransientNotificationError = (errorOrResponse) => {
    const status = Number(
      errorOrResponse?.response?.status ||
      errorOrResponse?.response?.data?.code ||
      errorOrResponse?.code ||
      errorOrResponse?.statusCode
    );
    const details = [
      errorOrResponse?.message,
      errorOrResponse?.error,
      errorOrResponse?.details,
      errorOrResponse?.response?.data?.message,
      errorOrResponse?.response?.data?.error,
      errorOrResponse?.response?.data?.details,
    ].filter(Boolean).join(' ');

    return [429, 502, 503, 504].includes(status) ||
      /bad gateway|webservice currently unavailable|upstream|timeout|temporarily unavailable|network error/i.test(details);
  };

  // Wrapper function to show a notification with a specific type and optional icon
  const showErrorNotification = useCallback(({ message, description, duration = 0, type = 'error', icon = null }) => {
    const isLoginPage = window.location.pathname === '/login';
    if (isLoginPage) {
      return; // Do not show any notification on the login page
    }

    // Check if this is a duplicate of the last shown error notification
    if (lastErrorNotificationRef.current.message === message && lastErrorNotificationRef.current.description === description) {
      return; // Do not show the notification
    }

    // Suppress specific authentication errors on the login page (this block is now redundant due to the above, but kept for context if needed)
    const isAuthError = message === 'Authentication Error' && description === 'Your session has expired. Please log in again.';
    const isSessionExpiredError = message === 'Session Expired' && description === 'Your session has expired. Please log in again.';

    if (isLoginPage && (isAuthError || isSessionExpiredError)) {
      return; // Do not show the notification
    }

    if (api && api[type]) {
      const notificationOptions = {
        message: message || 'Error',
        description: description || 'An unexpected error occurred.',
        placement: 'topRight',
        duration: duration
      };

      if (icon) {
        notificationOptions.icon = icon;
      }

      api[type](notificationOptions);
      // Update the ref with the details of the notification just shown
      lastErrorNotificationRef.current = { message, description };
    } else {
      console.error(`Ant Design notification API for type '${type}' not available.`);
    }
  }, [api]);

  // Warning notification helper - dismissible by key
  const showWarningNotification = useCallback(({ message, description, duration = 0, key }) => {
    const isLoginPage = window.location.pathname === '/login';
    if (isLoginPage) return;

    if (api) {
      api.warning({
        message: message || 'Warning',
        description: description || 'A warning occurred.',
        placement: 'topRight',
        duration: duration,
        key,
      });
    }
  }, [api]);

  const notify = useCallback((type, message, options = {}) => {
    const { key, description, suppressHidden = true } = options;
    if (window.location.pathname === '/login') return;
    if (suppressHidden && type !== 'error' && document.visibilityState === 'hidden') return;
    if (api && api[type]) {
      api[type]({
        key,
        message,
        description,
        placement: 'topRight',
        duration: type === 'error' ? 0 : 4.5,
      });
    }
  }, [api]);

  // Check if user has credentials configured
  const checkCredentialsStatus = async () => {
    // Skip if on login page or no user is logged in - prevents 401 errors
    const isLoginPage = window.location.pathname === '/login';
    if (isLoginPage) {
      setCredentialsConfigured(null);
      return null;
    }
    
    if (!userId) {
      setCredentialsConfigured(null);
      return null;
    }

    // Note: Auth token is now in httpOnly cookie, automatically sent with withCredentials
    // No need to check localStorage for token

    try {
      const response = await apiService.settings.getCredentialsStatus();
      // Handle nested response structure: response.data.data.configured
      const hasCreds = response?.data?.data?.configured || response?.data?.configured || false;
      setCredentialsConfigured(hasCreds);
      return hasCreds;
    } catch (error) {
      console.error('[NotificationContext] Error checking credentials status:', error);
      // On error, assume credentials might be configured to avoid false alerts
      setCredentialsConfigured(true);
      return true;
    }
  };

  const fetchNotifications = async (filters = {}) => {
    const { background = false, ...filterOverrides } = filters;
    // Check credentials first
    const hasCreds = await checkCredentialsStatus();
    if (!hasCreds) {
      setNotifications([]);
      setUnreadCount(0);
      return;
    }

    setIsLoadingNotifications(true);
    try {
      // Merge current filters with provided filters
      const currentFilters = { ...notificationFilters, ...filterOverrides };

      // Build API parameters
      const apiParams = {
        pageNo: currentFilters.pageNo,
        pageSize: currentFilters.pageSize,
        ...(background ? { background: 'true' } : {})
      };

      if (currentFilters.dateFrom) {
        apiParams.dateFrom = currentFilters.dateFrom.toISOString();
      }
      if (currentFilters.dateTo) {
        apiParams.dateTo = currentFilters.dateTo.toISOString();
      }
      const VALID_TYPE_IDS = ['3', '6', '7', '8', '10', '11', '15', '26', '33', '34', '35'];
      if (currentFilters.type && VALID_TYPE_IDS.includes(String(currentFilters.type))) {
        apiParams.type = currentFilters.type;
      }
      if (currentFilters.language) {
        apiParams.language = currentFilters.language;
      }
      if (currentFilters.status) {
        apiParams.status = currentFilters.status;
      }


      const backendResponse = await apiService.irb.getNotifications(apiParams);

      let myInvoisNotificationsArray = [];
      let metadata = null;

      if (backendResponse && backendResponse.status === 'success' && backendResponse.data) {
        if (Array.isArray(backendResponse.data.result)) {
          myInvoisNotificationsArray = backendResponse.data.result;
        }
        if (backendResponse.data.metadata) {
          metadata = backendResponse.data.metadata;
          setNotificationMetadata(metadata);
        }
      } else if (backendResponse && backendResponse.status !== 'success' && (backendResponse.message || backendResponse.error)) {
        if (isTransientNotificationError(backendResponse)) {
          console.warn('[NotificationContext] Notifications temporarily unavailable; keeping the last known notification state.', {
            status: backendResponse.code || backendResponse.statusCode,
            message: backendResponse.message || backendResponse.error,
          });
          setIsLoadingNotifications(false);
          return;
        }

        console.error('[NotificationContext] Backend reported an error fetching notifications:', backendResponse);
        showErrorNotification({
          message: 'Backend Error Fetching Notifications',
          description: backendResponse.message || backendResponse.error || 'An unknown error occurred.',
          type: 'error',
        });
        setIsLoadingNotifications(false);
        return;
      } else {
        console.warn('[NotificationContext] Fetched notifications data is not in the expected MyInvois structure. Full backend response:', backendResponse);
        setIsLoadingNotifications(false);
        return;
      }

      // Merge DB read IDs (cross-device) with localStorage (offline fallback)
      const dbReadIds = backendResponse.data?.readIds || [];
      const localReadIds = JSON.parse(localStorage.getItem(getUserStorageKey('readNotificationIds')) || '[]');
      const readNotificationIds = [...new Set([...dbReadIds, ...localReadIds])];
      // Persist merged set back to localStorage for offline use
      localStorage.setItem(getUserStorageKey('readNotificationIds'), JSON.stringify(readNotificationIds));

      // Map the MyInvois Notification object structure to the frontend's expected format
      const formattedNotifications = myInvoisNotificationsArray.map(myInvoisNotif => {
        // Determine Title
        const assignedTitle = myInvoisNotif.typeName || myInvoisNotif.notificationSubject || 'Notification';

        // Determine Message and if it's HTML
        let assignedMessage;
        let isHtml = false;

        if (typeof myInvoisNotif.finalMessage === 'string' && myInvoisNotif.finalMessage.trim().length > 0) {
          assignedMessage = myInvoisNotif.finalMessage;
          isHtml = true;
        } else {
          assignedMessage = myInvoisNotif.notificationSubject || (myInvoisNotif.typeName ? `Details for: ${myInvoisNotif.typeName}` : 'Notification details are not directly displayable.');
          isHtml = false;
        }

        return {
          id: myInvoisNotif.notificationId,
          title: assignedTitle,
          message: assignedMessage,
          isHtml: isHtml,
          type: myInvoisNotif.typeName ? myInvoisNotif.typeName.toLowerCase().replace(/\s+/g, '_') : 'default',
          read: readNotificationIds.includes(myInvoisNotif.notificationId) || myInvoisNotif.readStatus || false,
          timestamp: myInvoisNotif.creationDateTime || myInvoisNotif.receivedDateTime || new Date().toISOString(),
          originalData: myInvoisNotif
        };
      });


      // Desktop push for new critical notifications (only on page 1 refresh, not initial load)
      if (currentFilters.pageNo === 1 && knownNotificationIdsRef.current.size > 0) {
        formattedNotifications.forEach(notif => {
          const typeId = String(notif.originalData?.typeId || '');
          if (
            !knownNotificationIdsRef.current.has(notif.id) &&
            DESKTOP_PUSH_TYPE_IDS.includes(typeId) &&
            notifPrefsRef.current.desktopEnabled !== false &&
            typeof Notification !== 'undefined' &&
            Notification.permission === 'granted'
          ) {
            const body = (notif.message || '').replace(/<[^>]*>/g, '').substring(0, 120);
            new Notification(notif.title, { body, icon: '/logo.png' });
          }
        });
      }
      // Update known IDs set
      formattedNotifications.forEach(n => knownNotificationIdsRef.current.add(n.id));

      const groupedNotifications = groupDocumentNotifications(formattedNotifications);

      // Filter out muted types based on user preferences
      const mutedTypes = notifPrefsRef.current.mutedTypes || [];
      const visibleNotifications = mutedTypes.length > 0
        ? groupedNotifications.filter(n => !mutedTypes.includes(String(n.originalData?.typeId || '')))
        : groupedNotifications;

      // For pagination, append if not page 1, otherwise replace
      if (currentFilters.pageNo === 1) {
        setNotifications(visibleNotifications);
      } else {
        setNotifications(prev => [...prev, ...visibleNotifications]);
      }

      // Update filters state
      setNotificationFilters(currentFilters);

      // Calculate unread count from visible (non-muted) notifications only
      if (currentFilters.pageNo === 1) {
        const calculatedUnreadCount = visibleNotifications.filter(n => !n.read).length;
        setUnreadCount(calculatedUnreadCount);
        localStorage.setItem(getUserStorageKey('unreadCount'), calculatedUnreadCount.toString());
      }

    } catch (error) {
      const transientNotificationError = isTransientNotificationError(error);
      if (transientNotificationError) {
        console.warn('[NotificationContext] Notifications temporarily unavailable; keeping the last known notification state.', {
          status: error.response?.status || error.response?.data?.code,
          message: error.response?.data?.message || error.message,
        });
        setIsLoadingNotifications(false);
        return;
      }

      console.error('Failed to fetch notifications via backend:', error);

      // Check if this is a credentials not configured error (403 with specific code)
      const isCredentialsNotConfigured = error.response?.status === 403 && 
                                        error.response?.data?.code === 'CREDENTIALS_NOT_CONFIGURED';
      
      // Check if this is a credentials decryption error (500 with specific message)
      const isCredentialsError = error.response?.data?.message?.includes('Cannot decrypt credentials') ||
                                 error.response?.data?.message?.includes('Server encryption key or stored IV is missing') ||
                                 error.response?.data?.error?.includes('Cannot decrypt credentials') ||
                                 error.message?.includes('Cannot decrypt credentials');

      // Suppress error notification for credentials issues - we'll show the dashboard alert instead
      if (isCredentialsNotConfigured || isCredentialsError) {
        credentialsErrorDetectedRef.current = true; // Mark as detected to prevent retry loops
        setCredentialsConfigured(false);
        setNotifications([]);
        setUnreadCount(0);
        setIsLoadingNotifications(false);
        return;
      }

      let errorMessage = 'An unexpected error occurred while fetching notifications.';
      let errorDescription = '';

      if (error.response && error.response.status === 401) {
        errorMessage = 'Authentication Error';
        errorDescription = 'Your session has expired. Please log in again.';
      } else if (error.response && error.response.status) {
        errorMessage = `Error ${error.response.status}`;

        if (error.response.data) {
          errorMessage = 'API Error';
          if (typeof error.response.data === 'string') {
            errorDescription = error.response.data;
          } else if (error.response.data.message) {
            errorDescription = error.response.data.message;
          } else if (error.response.data.error) {
            errorDescription = error.response.data.error;
          } else if (error.response.statusText) {
            errorDescription = error.response.statusText;
          }
        }

        if (errorDescription.includes('Failed to get taxpayer token (MyInvois client error 400): invalid_client')) {
          errorDescription = 'Failed to get taxpayer token. Please go to the Settings page and set up your Client ID and Client Secret.';
        }
      } else if (error.code === 'ECONNABORTED') {
        errorMessage = 'Connection Timeout';
        errorDescription = 'The server took too long to respond. Please try again later.';
      } else if (error.message) {
        errorDescription = error.message;
      }

      showErrorNotification({
        message: errorMessage,
        description: errorDescription,
        type: 'error',
      });
    } finally {
      setIsLoadingNotifications(false);
    }
  };


  // Clear notifications when user changes (logout/login)
  useEffect(() => {
    if (!userId) {
      // User logged out, clear notifications
      setNotifications([]);
      setUnreadCount(0);
      setCredentialsConfigured(null);
      return;
    }

    // User logged in or changed, load their stored unread count
    const storedCount = localStorage.getItem(getUserStorageKey('unreadCount'));
    setUnreadCount(storedCount ? parseInt(storedCount, 10) : 0);
    setNotifications([]);
    setCredentialsConfigured(null);
  }, [userId]);

  // Track if we've detected credentials error to prevent infinite loops
  const credentialsErrorDetectedRef = useRef(false);

  // Fetch notifications when user is available and not on login page
  useEffect(() => {
    const isLoginPage = window.location.pathname === '/login';
    if (isLoginPage || !userId) {
      return;
    }

    // Reset credentials error detection on user change
    credentialsErrorDetectedRef.current = false;

    // Check credentials first, then fetch if configured
    const initFetch = async () => {
      // Skip if we already detected a credentials error
      if (credentialsErrorDetectedRef.current) {
        return;
      }

      const hasCreds = await checkCredentialsStatus();
      if (hasCreds) {
        fetchNotifications({ background: true });
      } else {
        setNotifications([]);
        setUnreadCount(0);
      }
    };

    initFetch();

    // Poll for new notifications every 5 minutes
    const interval = setInterval(() => {
      // Skip polling if credentials error was detected
      if (credentialsErrorDetectedRef.current) {
        return;
      }
      // Only poll if credentials are configured
      if (credentialsConfiguredRef.current === true) {
        fetchNotifications({ background: true });
      } else {
      }
    }, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, [userId]); // Only depend on userId, not credentialsConfigured

  // Subscribe to real-time notification events via WebSocket
  useEffect(() => {
    if (!userId) return;
    const handleNewNotification = () => {
      fetchNotifications({ pageNo: 1, background: true });
    };
    websocketService.on('notification:new', handleNewNotification);
    return () => websocketService.off('notification:new', handleNewNotification);
  }, [userId]);

  // Request browser notification permission after login
  useEffect(() => {
    if (
      userId &&
      notifPrefs.desktopEnabled !== false &&
      typeof Notification !== 'undefined' &&
      Notification.permission === 'default'
    ) {
      Notification.requestPermission();
    }
  }, [userId, notifPrefs.desktopEnabled]);


  const markAsRead = async (notificationId) => {
    const targetNotification = notifications.find(notificationItem => notificationItem.id === notificationId);
    const notificationIds = targetNotification?.groupedIds?.length ? targetNotification.groupedIds : [notificationId];

    // Update local state first (optimistic update)
    setNotifications(prevNotifications => {
      const updatedNotifications = prevNotifications.map(notif =>
        notificationIds.includes(notif.id) ? { ...notif, read: true } : notif
      );

      // Get current read notification IDs from localStorage (user-specific)
      const readNotificationIds = JSON.parse(localStorage.getItem(getUserStorageKey('readNotificationIds')) || '[]');
      // Add the new notificationId if it's not already there
      notificationIds.forEach(id => {
        if (!readNotificationIds.includes(id)) {
          readNotificationIds.push(id);
        }
      });
      localStorage.setItem(getUserStorageKey('readNotificationIds'), JSON.stringify(readNotificationIds));

      // Recalculate unread count based on updatedNotifications
      const calculatedUnreadCount = updatedNotifications.filter(n => !n.read).length;
      setUnreadCount(calculatedUnreadCount);
      localStorage.setItem(getUserStorageKey('unreadCount'), calculatedUnreadCount.toString());

      return updatedNotifications;
    });

    // Call backend API to mark as read (simple logging)
    try {
      await Promise.all(notificationIds.map(id => apiService.irb.markNotificationAsRead(id)));
    } catch (error) {
      // Silently fail - backend is just for logging, frontend handles the actual state
    }
  };

  const markAllAsRead = async () => {
    // Update local state first (optimistic update)
    setNotifications(prevNotifications => {
      const allIds = prevNotifications.flatMap(n => n.groupedIds?.length ? n.groupedIds : [n.id]);
      
      // Get current read notification IDs from localStorage (user-specific)
      const readNotificationIds = JSON.parse(localStorage.getItem(getUserStorageKey('readNotificationIds')) || '[]');
      
      // Add all notification IDs
      allIds.forEach(id => {
        if (!readNotificationIds.includes(id)) {
          readNotificationIds.push(id);
        }
      });
      
      localStorage.setItem(getUserStorageKey('readNotificationIds'), JSON.stringify(readNotificationIds));

      // Mark all as read
      const updatedNotifications = prevNotifications.map(notif => ({ ...notif, read: true }));

      // Update unread count
      setUnreadCount(0);
      localStorage.setItem(getUserStorageKey('unreadCount'), '0');

      return updatedNotifications;
    });

    // Call backend API to mark all as read (simple logging)
    try {
      await apiService.irb.markAllNotificationsAsRead();
    } catch (error) {
      // Silently fail - backend is just for logging, frontend handles the actual state
    }
  };

  const updateFilters = (newFilters) => {
    setNotificationFilters(prev => ({ ...prev, ...newFilters, pageNo: 1 })); // Reset to page 1 when filters change
  };

  const loadMoreNotifications = () => {
    if (notificationMetadata && notificationMetadata.hasNext && !isLoadingNotifications) {
      fetchNotifications({ pageNo: notificationFilters.pageNo + 1 });
    }
  };

  const refreshNotifications = () => {
    fetchNotifications({ pageNo: 1 });
  };

  const value = {
    notifications,
    unreadCount,
    notificationMetadata,
    notificationFilters,
    isLoadingNotifications,
    credentialsConfigured,
    notificationPreferences: notifPrefs,
    loadNotificationPreferences,
    saveNotificationPreferences,
    checkCredentialsStatus,
    fetchNotifications,
    refreshNotifications,
    loadMoreNotifications,
    updateFilters,
    markAsRead,
    markAllAsRead,
    showErrorNotification,
    showWarningNotification,
    notify,
    api, // Expose the Ant Design notification API
  };

  return (
    <NotificationContext.Provider value={value}>
      {contextHolder}
      {children}
    </NotificationContext.Provider>
  );
};

export const useNotifications = () => {
  const context = useContext(NotificationContext);
  if (context === undefined) {
    throw new Error('useNotifications must be used within a NotificationProvider');
  }
  return context;
};
